import { useEffect, useMemo, useState, type ReactNode, type FormEvent } from 'react';
import { apiRequest } from '../../api/client.js';
import { RecordPicker } from '../../components/RecordPicker.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

/**
 * M20 L20.08 — API key management.
 *
 * Reads `GET /v1/integration/api-keys` (key hashes are already masked
 * server-side; plaintext is only ever returned once at issue/rotate time).
 * Issue (`POST`), rotate (`POST :id/rotate`) and revoke (`POST :id/revoke`)
 * are guarded by `API_KEY_MANAGE`; the plaintext key is surfaced once here.
 */
interface ApiKeyRow {
  readonly apiKeyId: string;
  readonly consumerCode: string | null;
  readonly consumerName: string | null;
  readonly keyPrefix: string;
  readonly status: string;
  readonly lastUsedAt: string | null;
  readonly expiresAt: string | null;
  readonly revokedAt: string | null;
}

export function ApiKeysPage(): ReactNode {
  const { t } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const [rows, setRows] = useState<ApiKeyRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const [subscriptionId, setSubscriptionId] = useState('');
  const [issuing, setIssuing] = useState(false);
  const [plaintextKey, setPlaintextKey] = useState<string | null>(null);

  const load = (): void => {
    setError(null);
    apiRequest<{ data: ApiKeyRow[] }>('/v1/integration/api-keys', { token })
      .then((r) => setRows(r.data ?? []))
      .catch((e) => { setError((e as Error).message); setRows([]); });
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  // Status options are derived from the loaded data (no hardcoded enum).
  const statuses = useMemo(() => {
    const set = new Set<string>();
    (rows ?? []).forEach((k) => set.add(k.revokedAt ? 'REVOKED' : k.status));
    return ['', ...Array.from(set).sort()];
  }, [rows]);

  const visible = useMemo(() => {
    if (!rows) return null;
    if (!status) return rows;
    return rows.filter((k) => (k.revokedAt ? 'REVOKED' : k.status) === status);
  }, [rows, status]);

  const onIssue = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setIssuing(true);
    setPlaintextKey(null);
    try {
      const res = await apiRequest<{ plaintextKey?: string }>('/v1/integration/api-keys', {
        method: 'POST', token, body: { subscriptionId },
      });
      setPlaintextKey(res.plaintextKey ?? null);
      setSubscriptionId('');
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIssuing(false);
    }
  };

  const act = async (id: string, action: 'rotate' | 'revoke'): Promise<void> => {
    setBusyId(id + action);
    try {
      const res = await apiRequest<{ plaintextKey?: string }>(`/v1/integration/api-keys/${id}/${action}`, {
        method: 'POST', token,
      });
      if (action === 'rotate' && res.plaintextKey) setPlaintextKey(res.plaintextKey);
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <main aria-label="api-keys">
      <h1 style={{ fontSize: 20, marginBottom: 4 }}>{t('m20.apiKeys.title')}</h1>
      <p style={{ color: 'var(--color-text-muted)', marginBottom: 16, fontSize: 13 }}>
        {t('m20.apiKeys.subtitle')}
      </p>

      <form onSubmit={onIssue} aria-label="api-key-issue-form" style={{ marginBottom: 16 }}>
        <RecordPicker<{ subscriptionId: string; consumerId: string; consumerName: string; status: string }>
          endpoint="/v1/integration/subscriptions"
          idField="subscriptionId"
          label={t('m20.apiKeys.subscriptionId')}
          testId="issue-subscription"
          value={subscriptionId}
          optionLabel={(s) => `${s.consumerId} \u2014 ${s.consumerName} (${s.status})`}
          onPick={setSubscriptionId}
          emptyText="No subscription on file"
        />
        <button type="submit" disabled={issuing}>{issuing ? t('m20.apiKeys.issuing') : t('m20.apiKeys.issueKey')}</button>
      </form>

      {plaintextKey && (
        <p role="status" data-testid="apikey-plaintext" style={{ fontFamily: 'monospace', color: 'var(--color-success)' }}>
          {t('m20.apiKeys.plaintext', { key: plaintextKey })}
        </p>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
        <label htmlFor="status-filter" style={{ fontSize: 12 }}>{t('m20.apiKeys.statusLabel')}</label>
        <select id="status-filter" aria-label="status-filter" value={status} onChange={(e) => setStatus(e.target.value)}
                style={{ padding: '5px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }}>
          {statuses.map((s) => <option key={s || 'all'} value={s}>{s || t('m20.apiKeys.all')}</option>)}
        </select>
      </div>

      {error && <p role="alert" data-testid="apikeys-error" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {!visible ? (
        <p data-testid="apikeys-loading">{t('m20.apiKeys.loading')}</p>
      ) : visible.length === 0 ? (
        <p data-testid="apikeys-empty" style={{ color: 'var(--color-text-muted)' }}>{t('m20.apiKeys.empty')}</p>
      ) : (
        <table aria-label="apikeys-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--color-border-default)', textAlign: 'left' }}>
              <th style={{ padding: '8px 4px' }}>{t('m20.apiKeys.colKeyPrefix')}</th>
              <th style={{ padding: '8px 4px' }}>{t('m20.apiKeys.colSubscription')}</th>
              <th style={{ padding: '8px 4px' }}>{t('m20.apiKeys.colStatus')}</th>
              <th style={{ padding: '8px 4px' }}>{t('m20.apiKeys.colLastUsed')}</th>
              <th style={{ padding: '8px 4px' }}>{t('m20.apiKeys.colExpires')}</th>
              <th style={{ padding: '8px 4px' }}>{t('m20.apiKeys.colActions')}</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((k) => {
              const revoked = Boolean(k.revokedAt);
              return (
                <tr key={k.apiKeyId} data-testid={`apikey-row-${k.apiKeyId}`} style={{ borderBottom: '1px solid var(--color-border-default)' }}>
                  <td style={{ padding: '8px 4px', fontFamily: 'monospace' }}>{k.keyPrefix}…</td>
                  <td style={{ padding: '8px 4px' }}>{[k.consumerCode, k.consumerName].filter(Boolean).join(' \u2014 ')}</td>
                  <td style={{ padding: '8px 4px' }}>{revoked ? 'REVOKED' : k.status}</td>
                  <td style={{ padding: '8px 4px' }}>{at(k.lastUsedAt)}</td>
                  <td style={{ padding: '8px 4px' }}>{at(k.expiresAt)}</td>
                  <td style={{ padding: '8px 4px' }}>
                    {!revoked && (
                      <>
                        <button type="button" aria-label={`rotate-${k.apiKeyId}`} disabled={busyId === k.apiKeyId + 'rotate'} onClick={() => void act(k.apiKeyId, 'rotate')}>{t('m20.apiKeys.rotate')}</button>
                        <button type="button" aria-label={`revoke-${k.apiKeyId}`} disabled={busyId === k.apiKeyId + 'revoke'} onClick={() => void act(k.apiKeyId, 'revoke')}>{t('m20.apiKeys.revoke')}</button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </main>
  );
}
